import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Box } from "@mui/material";
import { ChesslaBoard } from "@/shared/components/chessboard";
import type { ChesslaBoardProps } from "@/shared/components/chessboard/ChesslaBoard";
import {
  BoardEvidenceWheel,
  type BoardEvidenceFocus,
  type BoardEvidenceItem,
} from "@/shared/components/chessboard/BoardEvidenceWheel";
import { DEFAULT_REVIEW_BOARD_SETTINGS, type ReviewBoardSettings } from "./review-board-settings";
import { relationshipPresentation } from "./relationship-evidence";
import { RelationshipArrowHover } from "./RelationshipArrowHover";

export interface ChesslaBoardSurfaceProps extends ChesslaBoardProps {
  settings?: ReviewBoardSettings;
  relationships?: boolean;
  relationshipArrows?: boolean;
  relationshipFocus?: BoardEvidenceFocus | null;
  evidence?: (relationships: BoardEvidenceItem[]) => ReactNode;
}

/** Position-only presentation. Loading, analysis and verified playback stay with the page adapter. */
export const ChesslaBoardSurface = ({
  settings = DEFAULT_REVIEW_BOARD_SETTINGS,
  relationships = true,
  relationshipArrows = true,
  relationshipFocus,
  evidence,
  width = "100%",
  position,
  orientation = "white",
  arrows = [],
  squareStyles = {},
  onSquareClick,
  ...props
}: ChesslaBoardSurfaceProps) => {
  const [selected, setSelected] = useState<BoardEvidenceFocus | null>(null);
  const [active, setActive] = useState<BoardEvidenceFocus | null>(null);
  useEffect(() => {
    setSelected(null);
    setActive(null);
  }, [position]);
  const focus = relationshipFocus !== undefined ? relationshipFocus : active?.fen === position ? active : selected;
  const square = focus?.fen === position ? focus.square : null;
  const presentation = useMemo(
    () => relationshipPresentation(position, settings, square),
    [position, settings, square],
  );
  const items = relationships ? presentation.items : [];
  const visibleArrows = [...(relationships && relationshipArrows ? presentation.arrows : []), ...arrows];
  return (
    <Box
      data-chessla-board-surface
      sx={{
        position: "relative",
        width,
        maxWidth: "100%",
        aspectRatio: "1 / 1",
        minWidth: 0,
        "& [data-relationship-hovered]": { filter: "brightness(1.3) drop-shadow(0 0 2px currentColor)" },
      }}
    >
      <ChesslaBoard
        {...props}
        position={position}
        orientation={orientation}
        width="100%"
        arrows={visibleArrows}
        squareStyles={{ ...(relationships && relationshipArrows ? presentation.squareStyles : {}), ...squareStyles }}
        onSquareClick={(next) => {
          setSelected((current) =>
            current?.fen === position && current.square === next ? null : { fen: position, square: next },
          );
          onSquareClick?.(next);
        }}
      />
      {visibleArrows.length > 0 && (
        <RelationshipArrowHover arrows={visibleArrows} fen={position} orientation={orientation} />
      )}
      {evidence ? (
        evidence(items)
      ) : (
        <BoardEvidenceWheel items={items} displayFen={position} orientation={orientation} onActiveChange={setActive} />
      )}
    </Box>
  );
};
